import { useState } from 'react'
import { Button, Stack, CircularProgress, Alert } from '@mui/material'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { useQueryClient } from '@tanstack/react-query'
import { useApiClient } from '~/api/useApiClient'
import { pinToSheet } from '~/util/export/chartData/pinToSheet'
import { buildWorkbook, errorSheet } from '~/util/export/chartData/buildWorkbook'
import { sanitizeFilename } from '~/pin/exportToJson'
import { errorString } from '~/errors/handleApiError'
import type { PinV1 } from '~/pin/PinV1'
import { DIALOGS } from '~/components/Charts/chrome/chartChrome'

interface CollectionDataExportButtonProps {
  pins: PinV1[]
  filenameBase: string
  disabled?: boolean
}

/** Whole-collection data export: one .xlsx workbook, one sheet per pin (Task 10). A per-pin
 *  pinToSheet failure is never dropped or allowed to abort the batch — it becomes a one-cell
 *  error-note sheet in the workbook, so the rest of the collection still exports (no silent
 *  fallbacks). */
export function CollectionDataExportButton({
  pins,
  filenameBase,
  disabled = false,
}: CollectionDataExportButtonProps) {
  const client = useApiClient()
  const queryClient = useQueryClient()
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const run = async () => {
    setError(null)
    setPending(true)
    try {
      const sheets = await Promise.all(
        pins.map(async (pin) => {
          try {
            return await pinToSheet(pin, { client, queryClient })
          } catch (e) {
            return errorSheet(
              sanitizeFilename(pin.render_hint.title ?? pin.walk_call.kpi),
              errorString(e) ?? 'Export failed',
            )
          }
        }),
      )
      await buildWorkbook(sheets, `${filenameBase}.xlsx`)
    } catch (err) {
      console.error('[CollectionDataExportButton] export failed', err)
      setError(DIALOGS.exportFailed)
    } finally {
      setPending(false)
    }
  }

  const isDisabled = disabled || pending || pins.length === 0

  return (
    <Stack spacing={0.5} alignItems="flex-end">
      <Button
        size="small"
        startIcon={pending ? <CircularProgress size={16} /> : <FileDownloadIcon />}
        onClick={run}
        disabled={isDisabled}
      >
        {DIALOGS.exportData}
      </Button>
      {error ? <Alert severity="error">{error}</Alert> : null}
    </Stack>
  )
}
